/**
 * SendMoneyTab - Send money via email, phone, or bank transfer
 * Refactored to use modular components
 * Original: 599 lines -> Refactored: ~220 lines
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  sendFunds, sendByPhone, sendToBank, getWalletBalances,
  getSavedRecipients, getPaymentMethods
} from '../../lib/api';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Loader2, Mail, Phone, Building2, AtSign } from 'lucide-react';

// Import modular components
import EmailTransferForm from './EmailTransferForm';
import PhoneTransferForm from './PhoneTransferForm';
import BankTransferForm from './BankTransferForm';
import TagTransferForm from './TagTransferForm';
import TransferSummary from './TransferSummary';
import SavedRecipients from './SavedRecipients';
import { COUNTRY_CODES, INITIAL_BANK_DETAILS } from './constants';

const SendMoneyTab = ({ user, balances, setBalances }) => {
  const [transferMethod, setTransferMethod] = useState('tag');  // Default to tag
  const [currency, setCurrency] = useState('USD');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [savedRecipients, setSavedRecipients] = useState([]);
  
  // Email form
  const [recipientEmail, setRecipientEmail] = useState('');
  
  // Tag form
  const [recipientTag, setRecipientTag] = useState('');
  const [resolvedTagUser, setResolvedTagUser] = useState(null);
  
  // Phone form
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // Bank form
  const [bankDetails, setBankDetails] = useState(INITIAL_BANK_DETAILS);
  const [bankEnabled, setBankEnabled] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    loadSavedRecipients();
    getPaymentMethods()
      .then((res) => setBankEnabled(res.data?.bank_transfer === true))
      .catch(() => setBankEnabled(false));
  }, []);

  const loadSavedRecipients = async () => {
    try {
      const res = await getSavedRecipients();
      setSavedRecipients(res.data);
    } catch (error) {
      console.debug('Failed to load saved recipients:', error.message);
    }
  };

  const getCurrentBalance = () => {
    return balances.find(b => b.currency === currency)?.balance || 0;
  };

  const calculateFee = () => {
    const amt = parseFloat(amount) || 0;
    if (transferMethod === 'bank') {
      const bankFee = amt * 0.025;
      const flatFee = bankDetails.bank_country === 'US' ? 1.0 : 5.0;
      return (bankFee + flatFee).toFixed(2);
    }
    return (amt * 0.015).toFixed(2);
  };

  const getTotalDeduction = () => {
    const amt = parseFloat(amount) || 0;
    const fee = parseFloat(calculateFee());
    return (amt + fee).toFixed(2);
  };

  const resetForm = () => {
    setAmount('');
    setNote('');
    setRecipientEmail('');
    setRecipientTag('');
    setResolvedTagUser(null);
    setPhoneNumber('');
    setBankDetails(INITIAL_BANK_DETAILS);
  };

  const handleSend = async () => {
    const amt = parseFloat(amount);
    
    if (!amt || amt <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const total = parseFloat(getTotalDeduction());
    if (total > getCurrentBalance()) {
      toast.error('Insufficient funds');
      return;
    }

    setLoading(true);
    try {
      if (transferMethod === 'tag') {
        if (!recipientTag || recipientTag.length < 4) {  // @ + 3 chars minimum
          toast.error('Please enter a valid @tag');
          setLoading(false);
          return;
        }
        if (!resolvedTagUser) {
          toast.error('Please enter a valid @tag that exists');
          setLoading(false);
          return;
        }
        const cleanTag = recipientTag.replace('@', '');
        await sendFunds(null, amt, currency, note || undefined, cleanTag);
        toast.success(`Sent $${amt.toFixed(2)} ${currency} to @${cleanTag}`);
      }
      else if (transferMethod === 'email') {
        if (!recipientEmail || !recipientEmail.includes('@')) {
          toast.error('Please enter a valid email address');
          setLoading(false);
          return;
        }
        if (recipientEmail.toLowerCase() === user?.email?.toLowerCase()) {
          toast.error('Cannot send to yourself');
          setLoading(false);
          return;
        }
        await sendFunds(recipientEmail, amt, currency, note || undefined);
        toast.success(`Sent $${amt.toFixed(2)} ${currency} to ${recipientEmail}`);
      } 
      else if (transferMethod === 'phone') {
        if (!phoneNumber || phoneNumber.length < 7) {
          toast.error('Please enter a valid phone number');
          setLoading(false);
          return;
        }
        const fullPhone = countryCode + phoneNumber.replace(/\D/g, '');
        try {
          await sendByPhone(fullPhone, amt, currency, note || undefined);
        } catch (err) {
          if (err.response?.status === 404) {
            const detail = String(err.response?.data?.detail || '');
            toast.error(detail.includes('verified') ? t('notices.phoneNotVerified') : t('notices.phoneNotRegistered'));
            setLoading(false);
            return;
          }
          throw err;
        }
        toast.success(`Sent $${amt.toFixed(2)} ${currency} to ${fullPhone}`);
      }
      else if (transferMethod === 'bank') {
        if (!bankDetails.account_holder_name || !bankDetails.account_number || !bankDetails.bank_name) {
          toast.error('Please fill in all required bank details');
          setLoading(false);
          return;
        }
        if (bankDetails.bank_country === 'US' && !bankDetails.routing_number) {
          toast.error('Routing number is required for US bank accounts');
          setLoading(false);
          return;
        }
        if (bankDetails.bank_country !== 'US' && !bankDetails.iban && !bankDetails.swift_code) {
          toast.error('IBAN or SWIFT code is required for international transfers');
          setLoading(false);
          return;
        }
        
        const result = await sendToBank(bankDetails, amt, currency, note || undefined);
        toast.success(`Bank transfer initiated! ${result.data.estimated_arrival}`, { duration: 6000 });
      }
      
      resetForm();
      
      // Refresh balances
      const balancesRes = await getWalletBalances();
      setBalances(balancesRes.data);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const selectSavedRecipient = (recipient) => {
    if (recipient.recipient_type === 'email') {
      setTransferMethod('email');
      setRecipientEmail(recipient.email);
    } else if (recipient.recipient_type === 'phone') {
      setTransferMethod('phone');
      const phone = recipient.phone_number || '';
      const matched = COUNTRY_CODES.find(c => phone.startsWith(c.code));
      if (matched) {
        setCountryCode(matched.code);
        setPhoneNumber(phone.replace(matched.code, ''));
      } else {
        setPhoneNumber(phone);
      }
    } else if (recipient.recipient_type === 'bank') {
      if (!bankEnabled) {
        toast.info(t('notices.bankUnavailable'));
        return;
      }
      setTransferMethod('bank');
      if (recipient.bank_details) {
        setBankDetails({
          ...bankDetails,
          ...recipient.bank_details,
          account_number: ''
        });
      }
    }
  };

  const isDisabled = loading || !amount || parseFloat(getTotalDeduction()) > getCurrentBalance();

  return (
    <div className="space-y-6" data-testid="send-money-tab">
      {/* Balance Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Available {currency} Balance</p>
              <p className="text-2xl font-heading font-bold" data-testid="send-balance">
                ${getCurrentBalance().toFixed(2)}
              </p>
            </div>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="w-32 rounded-xl" data-testid="send-currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {balances.map((balance) => (
                  <SelectItem key={balance.currency} value={balance.currency}>
                    {balance.symbol} {balance.currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Transfer Method Tabs */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Send Money</CardTitle>
          <CardDescription>Choose how you want to send funds</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs value={transferMethod} onValueChange={setTransferMethod}>
            <TabsList className={`grid w-full ${bankEnabled ? 'grid-cols-4' : 'grid-cols-3'} h-12 rounded-xl`}>
              <TabsTrigger value="tag" className="rounded-lg" data-testid="method-tag">
                <AtSign className="w-4 h-4 mr-2" />
                @Tag
              </TabsTrigger>
              <TabsTrigger value="email" className="rounded-lg" data-testid="method-email">
                <Mail className="w-4 h-4 mr-2" />
                Email
              </TabsTrigger>
              <TabsTrigger value="phone" className="rounded-lg" data-testid="method-phone">
                <Phone className="w-4 h-4 mr-2" />
                Phone
              </TabsTrigger>
              {bankEnabled && (
                <TabsTrigger value="bank" className="rounded-lg" data-testid="method-bank">
                  <Building2 className="w-4 h-4 mr-2" />
                  Bank
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="tag" className="space-y-4 mt-4">
              <TagTransferForm 
                recipientTag={recipientTag}
                setRecipientTag={setRecipientTag}
                onRecipientResolved={setResolvedTagUser}
              />
            </TabsContent>

            <TabsContent value="email" className="space-y-4 mt-4">
              <EmailTransferForm 
                recipientEmail={recipientEmail}
                setRecipientEmail={setRecipientEmail}
              />
            </TabsContent>

            <TabsContent value="phone" className="space-y-4 mt-4">
              <PhoneTransferForm 
                countryCode={countryCode}
                setCountryCode={setCountryCode}
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
              />
            </TabsContent>

            <TabsContent value="bank" className="space-y-4 mt-4">
              <BankTransferForm 
                bankDetails={bankDetails}
                setBankDetails={setBankDetails}
              />
            </TabsContent>
          </Tabs>

          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="amount"
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-12 pl-8 rounded-xl"
                min="0.01"
                step="0.01"
                data-testid="send-amount-input"
              />
            </div>
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note">Note (Optional)</Label>
            <Textarea
              id="note"
              placeholder="What's this for?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="rounded-xl resize-none"
              rows={2}
              data-testid="send-note-input"
            />
          </div>

          {/* Summary */}
          <TransferSummary
            amount={amount}
            currency={currency}
            transferMethod={transferMethod}
            bankCountry={bankDetails.bank_country}
            currentBalance={getCurrentBalance()}
          />

          {/* Send Button */}
          <Button
            onClick={handleSend}
            disabled={isDisabled}
            className="w-full h-12 rounded-xl"
            data-testid="send-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <ArrowUpRight className="w-4 h-4 mr-2" />
                {transferMethod === 'bank' ? 'Initiate Bank Transfer' : 'Send Money'}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Saved Recipients */}
      <SavedRecipients 
        savedRecipients={savedRecipients}
        onSelectRecipient={selectSavedRecipient}
      />
    </div>
  );
};

export default SendMoneyTab;
